/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import type { Guardian } from '@whizz/api-model/dist/@types/endpoints/Guardians';
import type { SubmitHandler } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import Input from '../../../../../components/input/Input';
import { useFieldArray, useForm } from 'react-hook-form';
import Button from '../../../../../components/button/Button';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../../util/vars';
import { model } from '@whizz/api-client';
import { Alert, MaterialIcon } from '../../../../../components';
import { useI18n } from '@whizz/react-i18n';
import { yupResolver } from '@hookform/resolvers/yup';
import { PARENT_MAILING_SCHEMA } from '../../../../../forms/validations';
import { Section } from './Components';

const PARENT_INITIAL_DATA = {
  name: '',
  email: '',
};

export default function ParentDataSection({ disabled, studentToken, onSuccess }: Props) {

  const parentsQuery = useQuery(
    [ QUERIES_IDS.PARENTS ],
    () => model.Guardians.get(studentToken ?? ''),
    {
      enabled: !disabled && studentToken != null,
      placeholderData: [],
    },
  );

  if (parentsQuery.data) {
    return (
      <ParentDataForm disabled={disabled} studentToken={studentToken} onSuccess={onSuccess} data={parentsQuery.data} />
    );
  }
  return null;
}

const ParentDataForm = ({ disabled, studentToken, onSuccess, data }: Props & { data: Guardian[] }) => {

  const { translate } = useI18n();
  const queryClient = useQueryClient();
  const [ error, setError ] = useState(null);
  const [ success, setSuccess ] = useState(false);

  const { mutate, isLoading } = useMutation(
    (formData: FormValues) => model.Guardians.put(studentToken ?? '', formData.parents),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([ QUERIES_IDS.PARENTS ]);
        setError(null);
        setSuccess(true);
        setTimeout(() => onSuccess(data.map((guardian) => guardian.email)), 1000);
      },
      onError: (axiosResponse: any) => setError(axiosResponse.response.data.context.hint),
    },
  );

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    mutate(data);
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      parents: data,
    },
    values: {
      name: '',
      parents: data,
    },
    resolver: yupResolver(PARENT_MAILING_SCHEMA),
  });

  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'parents',
  });

  useEffect(() => {
    // If there are no parent details and form hasn't been modified, add empty fields.
    if (!fields.length && !isDirty) {
      append(PARENT_INITIAL_DATA);
    }
  }, [ fields, isDirty ]);

  return (
    <>
      <Section index={2} disabled={disabled}>
        <p>{translate('pages.PARENT_EMAIL_PROCESS.PARENT_DATA.ENTER_DETAILS')}</p>
        <form onSubmit={handleSubmit(onSubmit)}>
          { /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
            /* @ts-ignore */ }
          {fields.map((field, index) => (
            <div key={field.id} className={'flex flex-col mb-md'}>
              <div className={'mb-5'}>
                <Input
                  {...register(`parents.${index}.name` as const, { required: true })}
                  label={translate('pages.PARENT_EMAIL_PROCESS.PARENT_DATA.FIELDS.name')}
                  id={`parents.${index}.name`}
                  disabled={disabled || isLoading || success}
                  icon={'Delete'}
                  iconColor={'error'}
                  iconPosition={'right-outside'}
                  onIconClick={() => remove(index)}
                />
              </div>
              {errors.parents && errors.parents[index]?.name?.message &&
                (
                  <Alert show type={'error'} className={'mb-5'}>
                    {errors.parents[index]?.name?.message}
                  </Alert>
                )
              }
              <div className={'mb-5'}>
                <Input
                  {...register(`parents.${index}.email` as const, { required: true })}
                  label={translate('pages.PARENT_EMAIL_PROCESS.PARENT_DATA.FIELDS.email')}
                  id={`parents.${index}.email`}
                  disabled={disabled || success}
                />
              </div>
              {errors.parents && errors.parents[index]?.email?.message &&
                (
                  <Alert show type={'error'} className={'mb-5'}>
                    {errors.parents[index]?.email?.message}
                  </Alert>
                )
              }
            </div>
          ))}

          <div className={'mb-5'}>
            <Button
              asLink
              type={'button'}
              icon={'Add'}
              onClick={() => append(PARENT_INITIAL_DATA)}
              disabled={disabled || isLoading || success}
            >
              {translate('pages.PARENT_EMAIL_PROCESS.PARENT_DATA.FIELDS.add')}
            </Button>
          </div>

          {null !== error && (
            <Alert show type={'error'} className={'mb-5'}>
              {error}
            </Alert>
          )}

          <div className={'mb-5'}>
            <Button
              isOutline
              isLoading={isLoading}
              color={'positive'}
              type={'submit'}
              disabled={disabled || isLoading || success || !(isDirty && isValid)}
            >
              {translate('pages.PARENT_EMAIL_PROCESS.PARENT_DATA.FIELDS.submit')}
            </Button>
          </div>
          {success && (
            <div className={'flex items-center gap-2'}>
              <MaterialIcon icon={GLOBAL_ICONS.tick} color={'positive'} />{translate('pages.PARENT_EMAIL_PROCESS.PARENT_UPDATE.SUCCESS')}
            </div>
          )}
        </form>
      </Section>
    </>
  );
};

interface Props {
  disabled?: boolean;
  studentToken: string | null;
  onSuccess: (mails: Guardian['email'][]) => void;
}

interface FormValues {
  name: string;
  parents: Guardian[];
}
